import {
	Component,
	HostListener,
	OnDestroy,
	OnInit,
	Renderer2
} from '@angular/core';
import { Location } from '@angular/common';
import { Subject } from 'rxjs/Subject';
import { SubscriptionModalComponent } from '@web-components/subscription/modals/subscription-modal/subscription-modal';
import { SNModalService } from '@web-services/modal/snModal.service';
import {
	CommunicationEvents,
	PlanConfig,
	SubscriptionModalState,
	SubscriptionStatus,
	SubscriptionStatusResponse
} from '@web-components/subscription/subscription.model';
import { IframeService } from '@services/helpers/iframe-service/iframe.service';
import { NewSubscriptionServiceDataProvider } from '@services/subscription/subscription.service';
import { distinctUntilChanged, finalize } from 'rxjs/operators';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MessageService } from '@services/helpers/message-service/message.service';
import { GeneralAction } from '@models/models';
import { BrandsDataServiceProvider } from '@services/brands/brands.data.service';

@Component({
	selector: 'subscription-choose-plan',
	template: require('./subscription-choose-plan.html'),
	styles: [require('./subscription-choose-plan.scss')]
})
export class SubscriptionChoosePlanComponent implements OnInit, OnDestroy {
	private urlToLoad: string;
	private modalRef: NgbModalRef;
	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private snModalService: SNModalService,
		private iframeService: IframeService,
		private renderer: Renderer2,
		private subscriptionService: NewSubscriptionServiceDataProvider,
		private messageService: MessageService,
		private brandsDataService: BrandsDataServiceProvider,
		public location: Location
	) {}

	ngOnInit(): void {
		this.attachFrameHandlers();
		this.listenForModalOpen();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	private openModal(url?: string): void {
		this.modalRef = this.snModalService.openModal(SubscriptionModalComponent, {
			windowClass: 'ng-bootstrap-modal modal-visible modal-liquid'
		});
		this.modalRef.componentInstance.urlToLoad = url;
	}

	private attachFrameHandlers(): void {
		this.onCheckoutModalBackEvent();
		this.onCheckoutModalOpenEvent();
		this.onCheckoutModalCloseEvent();
		this.onConfirmPaymentClose();
		this.onEditBillingModalIframeClose();
		this.onMessageReceived();
		this.onUpdatePaymentClose();
	}

	private listenForModalOpen(): void {
		this.subscriptionService.choosePlanModalOpenStatus
			.pipe(distinctUntilChanged())
			.subscribe((data: SubscriptionModalState<PlanConfig>): void => {
				if (data.state) {
					this.urlToLoad = data.payload ? data.payload.url : null;
					this.openModal(this.urlToLoad);
				}
			});
	}

	private successPaymentProcessed(data: SubscriptionStatusResponse): void {
		if (data) {
			const needToConfirmPayment: GeneralAction =
				data.data.actions.COMPLETE_PAYMENT;
			if (needToConfirmPayment) {
				this.messageService.showToaster('i_0', 'stripe');
				this.iframeService.setUrlIframe(null);
				setTimeout(
					() => this.iframeService.setUrlIframe(needToConfirmPayment.url),
					0
				);
			} else {
				const planName = data.data.currentPlan.planName || 'new plan';
				this.messageService.showToaster('s_0', 'subscription', planName);
				this.iframeService.setUrlIframe(null);
				this.brandsDataService.updateBrands().subscribe();
				this.redirectIfOnLogin();
			}
			this.subscriptionService.setSubscriptionData(data.data);
		}
	}

	private redirectIfOnLogin(): void {
		if (this.location.path(false) === '/login') {
			this.location.go('/');
		}
	}

	private onCheckoutModalBackEvent(): void {
		this.renderer.listen(
			'document',
			CommunicationEvents.planCheckoutBack,
			(): void => {
				this.iframeService.setUrlIframe(null);
				this.subscriptionService.setChoosePlanOpenModalStatus({
					state: true,
					payload: {
						url: this.urlToLoad
					}
				});
			}
		);
	}

	private onCheckoutModalOpenEvent(): void {
		this.renderer.listen(
			'document',
			CommunicationEvents.planCheckoutOpen,
			(): void => {
				if (this.modalRef) {
					this.subscriptionService.setChoosePlanOpenModalStatus({
						state: false,
						payload: null
					});
					this.modalRef.close();
				}
			}
		);
	}

	private onCheckoutModalCloseEvent(): void {
		this.renderer.listen(
			'document',
			CommunicationEvents.planCheckoutClose,
			data => this.successPaymentProcessed(data.detail.data)
		);
	}

	private onConfirmPaymentClose(): void {
		this.renderer.listen(
			'document',
			CommunicationEvents.confirmPaymentClose,
			(data: CustomEvent): void => {
				if (data.detail.paymentIntent) {
					this.subscriptionService
						.fetchSubscriptionStatus()
						.pipe(finalize(() => this.iframeService.setUrlIframe(null)))
						.subscribe((data: SubscriptionStatus): void => {
							this.successPaymentProcessed({ data, result: 'ok' });
						});
				} else {
					this.messageService.showToaster('e_8', 'stripe');
					this.iframeService.setUrlIframe(null);
				}
			}
		);
	}

	private onEditBillingModalIframeClose(): void {
		this.renderer.listen(
			'document',
			CommunicationEvents.billingUpdateClose,
			(data: CustomEvent): void => {
				this.iframeService.setUrlIframe(null);
				const status: SubscriptionStatusResponse = data.detail.data;
				if (status) {
					this.messageService.showToaster('i_1', 'subscription', 'current');
					this.subscriptionService.setSubscriptionData(status.data);
				}
			}
		);
	}

	private onMessageReceived(): void {
		this.renderer.listen(
			'document',
			CommunicationEvents.messageReceived,
			(data: CustomEvent): void => {
				this.iframeService.setUrlIframe(null);
				const status = data.detail;

				if (status) {
					this.messageService.showMessageFromBackend(
						status.messageType,
						status.title,
						status.text
					);
				}
			}
		);
	}

	private onUpdatePaymentClose(): void {
		this.renderer.listen(
			'document',
			CommunicationEvents.updatePaymentClose,
			(data: CustomEvent): void => {
				const status = data.detail.data;
				if (!status) {
					this.iframeService.setUrlIframe(null);
					return;
				}
				if (status.error) {
					this.messageService.showToaster('e_8', 'stripe');
					this.iframeService.setUrlIframe(null);
					return;
				}
				if (
					status.paymentIntent &&
					status.paymentIntent.status === 'succeeded'
				) {
					this.subscriptionService
						.fetchSubscriptionStatus()
						.pipe(finalize(() => this.iframeService.setUrlIframe(null)))
						.subscribe((data: SubscriptionStatus): void => {
							this.successPaymentProcessed({ data, result: 'ok' });
						});
				}
			}
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./web/modern/components/subscription/subscription-choose-plan/subscription-choose-plan.ts